/**
 * Key management API client
 */

import { fetchWithAuth } from './authClient';
import { AUTH_API_URL } from './config';

export async function getPasskeyRecoveryKeys(): Promise<Array<{ id: string; public_key: string }>> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/webauthn/recovery-keys`);
  if (!response.ok) throw new Error('Failed to fetch passkey recovery keys');
  return (await response.json()).keys;
}

export async function getPasskeyRecoverySeeds(credentialId: string, workspaceId?: string): Promise<Array<{
  workspace_id: string; profile_id: string; kid: string; wrapped_seed: string;
}>> {
  if (!credentialId.trim() || credentialId.length > 2000 || (workspaceId !== undefined && (!workspaceId.trim() || workspaceId.length > 200))) {
    throw new Error('Invalid passkey recovery identity');
  }
  const query = workspaceId ? `?${new URLSearchParams({ workspace_id: workspaceId })}` : '';
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}/seeds${query}`);
  if (!response.ok) throw new Error('Passkey recovery copy discovery failed; original keys were preserved');
  const data = await response.json();
  if (!Array.isArray(data?.seeds) || data.seeds.some((seed: any) => !seed ||
    typeof seed.workspace_id !== 'string' || !seed.workspace_id.trim() ||
    typeof seed.profile_id !== 'string' || !seed.profile_id.trim() || typeof seed.kid !== 'string' || !seed.kid.trim() ||
    typeof seed.wrapped_seed !== 'string' || !/^[A-Za-z0-9+/]{123}=$/.test(seed.wrapped_seed) ||
    (workspaceId !== undefined && seed.workspace_id !== workspaceId)) ||
    new Set(data.seeds.map((seed: any) => JSON.stringify([seed.workspace_id, seed.kid]))).size !== data.seeds.length) {
    throw new Error('Invalid passkey recovery copy receipt');
  }
  return data.seeds;
}

export async function storePasskeySeed(credentialId: string, seed: {
  workspace_id: string; profile_id: string; kid: string; wrapped_seed: string;
}): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}/seeds`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seeds: [seed] }),
  });
  if (!response.ok || !(await response.json()).success) throw new Error('Failed to store passkey recovery seed');
}

export interface WrappedSeed {
  workspace_id: string;
  profile_id: string;
  kid: string;
  wrapped_seed: string;
  wrap_algorithm: string;
  wrap_type?: 'device' | 'recovery'; // Type of wrapping (device key or recovery phrase)
  device_id?: string; // For device-wrapped seeds
  created_at: string;
}

export interface DeviceKey {
  device_id: string;
  public_key: string;
  algorithm: string;
  created_at: string;
}

/**
 * Store wrapped workspace seed
 */
export async function storeWrappedSeed(data: {
  workspace_id: string;
  profile_id: string;
  kid: string;
  wrapped_seed: string;
  wrap_algorithm: string;
  wrap_type?: 'device' | 'recovery'; // Type of wrapping (default: 'device')
  device_id?: string; // Optional: associate seed with specific device
}): Promise<WrappedSeed> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/keys/seeds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to store wrapped seed');
  }

  const result = await response.json();
  return result.wrapped_seed;
}

/**
 * Get wrapped seeds for current profile.
 *
 * Always pass device_id so the server returns only seeds for the requesting device —
 * prevents attempting to decrypt seeds wrapped for other devices.
 */
export async function getWrappedSeeds(
  workspaceId?: string,
  options?: { wrap_type?: 'device' | 'recovery'; device_id?: string; account_owned?: boolean }
): Promise<WrappedSeed[]> {
  const params = new URLSearchParams();
  if (workspaceId) params.set('workspace_id', workspaceId);
  if (options?.wrap_type) params.set('wrap_type', options.wrap_type);
  if (options?.device_id !== undefined) params.set('device_id', options.device_id);
  if (options?.account_owned) params.set('account_owned', 'true');

  const query = params.toString();
  const url = `${AUTH_API_URL}/api/keys/seeds${query ? `?${query}` : ''}`;

  const response = await fetchWithAuth(url);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get wrapped seeds');
  }

  const result = await response.json();
  return result.wrapped_seeds;
}

/**
 * Store device public key
 */
export async function storeDeviceKey(data: {
  device_id: string;
  public_key: string;
  algorithm: string;
}): Promise<DeviceKey> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/keys/devices`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to store device key');
  }

  const result = await response.json();
  return result.device_key;
}

/**
 * Get device public key by device ID
 */
export async function getDeviceKey(deviceId: string): Promise<DeviceKey | null> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/keys/devices/${deviceId}`);

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to get device key');
  }

  const result = await response.json();
  return result.device_key;
}

/**
 * Get member device keys for workspace (for key sharing)
 */
export async function getMemberDeviceKeys(
  workspaceId: string
): Promise<Array<{ profile_id: string; device_key: DeviceKey }>> {
  const response = await fetchWithAuth(
    `${AUTH_API_URL}/api/keys/workspace/${workspaceId}/member-keys`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get member device keys');
  }

  const result = await response.json();
  return result.member_keys;
}

/**
 * Get all device public keys for a profile.
 *
 * Used when sharing a workspace seed with a new member (including invited/pending ones)
 * before they have active workspace membership.
 */
export async function getProfileDeviceKeys(profileId: string): Promise<DeviceKey[]> {
  const response = await fetchWithAuth(
    `${AUTH_API_URL}/api/keys/profile/${profileId}/devices`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get profile device keys');
  }

  const result = await response.json();
  return result.device_keys;
}

export interface AccountWorkspaceInventory {
  accountId: string;
  workspaces: Array<{ id: string; profileId: string; encrypted: boolean; kid?: string;
    status: 'active' | 'invited'; keyState: 'initialized' | 'uninitialized' }>;
}
export async function getAccountWorkspaceInventory(): Promise<AccountWorkspaceInventory> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/keys/account-inventory`);
  if (!response.ok) throw new Error('Account workspace discovery failed; retry without resetting local data');
  return response.json();
}
export async function configurePhraseRecovery(publicKey: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/recovery/configure`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ public_key: publicKey }),
  });
  if (!response.ok) throw new Error('Recovery approval configuration failed. Existing recovery keys were preserved.');
}
export interface PhraseRecoverySeed { workspace_id: string; profile_id: string; kid: string; wrapped_seed: string; wrap_algorithm?: string }
export async function beginPhraseRecovery(publicKey: string): Promise<{
  ticket: string; proof: string; account_id: string; device_id: string; seeds: PhraseRecoverySeed[];
}> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/recovery/begin`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ public_key: publicKey }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Recovery challenge failed');
  return data;
}
export async function finishPhraseRecovery(ticket: string, proof: string, seeds: PhraseRecoverySeed[]): Promise<{
  success: true; status: 'approved'; account_id: string; device_id: string;
}> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/recovery/finish`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ticket, proof, seeds }),
  });
  const data = await response.json();
  if (!response.ok || data.success !== true || data.status !== 'approved') throw new Error(data.error || 'Recovery approval failed');
  return data;
}

/** Server-pinned phrase key. Copy coverage does not prove decryptability or sync. */
export interface PhraseRecoveryConfiguration {
  accountId: string;
  publicKey: string | null;
  workspaces: Array<{ id: string; profileId: string; recoveryCopyAvailable: boolean }>;
}
export async function getPhraseRecoveryConfiguration(): Promise<PhraseRecoveryConfiguration> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/recovery/configuration`);
  if (!response.ok) throw new Error('Recovery configuration discovery failed; original keys were preserved');
  const data: unknown = await response.json();
  const identifier = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0 && value.length <= 200;
  if (!data || typeof data !== 'object') throw new Error('Invalid recovery configuration receipt');
  const receipt = data as Partial<PhraseRecoveryConfiguration>;
  if (!identifier(receipt.accountId) || (receipt.publicKey !== null &&
    (typeof receipt.publicKey !== 'string' || !/^[A-Za-z0-9+/]{43}=$/.test(receipt.publicKey))) ||
    !Array.isArray(receipt.workspaces) || receipt.workspaces.some(workspace => !workspace ||
      !identifier(workspace.id) || !identifier(workspace.profileId) || typeof workspace.recoveryCopyAvailable !== 'boolean') ||
    new Set(receipt.workspaces.map(workspace => workspace.id)).size !== receipt.workspaces.length) {
    throw new Error('Invalid recovery configuration receipt');
  }
  return receipt as PhraseRecoveryConfiguration;
}
